import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon, DeleteIcon, Edit1Icon } from 'tdesign-icons-react';
import { Button, Image, Loading, SelectOption, Switch, Tag, Textarea, Tooltip } from 'tdesign-react';

import empty from '@/assets/image/editor/empty_bot.png';
import { EasyTip } from '@/components/easyTip';
import { DebounceSelect } from '@/components/form/materials/DebounceSelect';
import { NavIcon } from '@/components/icon';
import TipDialog, { ITipDialogRef } from '@/components/tipDialog';
import SvgIcon from '@/components/tools/svg-icon';
import { MissionType, MissionTypeNameMap } from '@/const/mission';
import { isAuthor, nodeCanDelete } from '@/editor/flowEditor/control/utils/disabled';
import { UseAutoSave } from '@/hooks/useAutoSave';
import { IBot, IUsedBot } from '@/interface';
import { fetchBotsOptions } from '@/pages/bot/fetch/fetchBot-config';
import BotInfo from '@/pages/bot/info';
import BotGeneratorInner from '@/pages/bot/info/botGenerator/botGeneratorInner';
import { CustomArrayQA, IQA } from '@/pages/bot/info/custom-form/customQA';
import { useTypedSelector } from '@/store';

import styles from './index.module.less';

interface ISceneBotsProps {
  value: Partial<IBot>[];
  onChange: (value: Partial<IBot>[]) => void;
  disabled?: boolean;
  usedBots: IUsedBot[];
  selectedBotIds: string[];
}

export const SceneBots: React.FC<ISceneBotsProps> = (props) => {
  const {
    value = [],
    onChange,
    disabled = false,
    usedBots = [],
    selectedBotIds = [],
  } = props;
  const [bots, setBots] = useState<Partial<IBot>[]>([]);
  const [curBotId, setCurBotId] = useState<string>('');
  const [chooseIds, setChooseIds] = useState<string[]>([]);
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [showPrompt, setShowPrompt] = useState<boolean>(false);
  const [showSelect, setShowSelect] = useState<boolean>(false);
  const [geneNum, setGeneNum] = useState<number>(0);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);

  // 补充人设
  const [prompt, setPrompt] = useState<{ [key: string]: string }>({});
  const [tempPrompt, setTempPrompt] = useState<string>('');
  // 规则限制
  const [instruction, setInstruction] = useState<{ [key: string]: string }>({});
  const [tempInstruction, setTempInstruction] = useState<string>('');
  // 对话示例
  const [definitions, setDefinitions] = useState<{ [key: string]: IQA[] }>({});
  const [tempDefinitions, setTempDefinitions] = useState<IQA[]>([{ q: '', a: '' }]);
  // 是否覆盖原人设
  const [cover, setCover] = useState<{ [key: string]: boolean }>({});
  const [tempCover, setTempCover] = useState<boolean>(false);

  const [visible, setVisible] = useState(false);
  const [editId, setEditId] = useState<string>();
  const botsRef = useRef(value);
  const addRef = useRef<HTMLDivElement>(null);
  const selectRef = useRef<HTMLDivElement>(null);
  const tips = useTypedSelector(store => store.tip) as any[];

  useEffect(() => {
    setBots(value);
    setChooseIds(value.map((e: any) => e.id));
    botsRef.current = value;

    const newPrompt: { [key: string]: string } = {};
    const newInstruction: { [key: string]: string } = {};
    const newDefinitions: { [key: string]: IQA[] } = {};
    const newCover: { [key: string]: boolean } = {};
    value.forEach((e: any) => {
      newPrompt[e.id] = e.prompt;
      newInstruction[e.id] = e.userInstructionBase;
      newDefinitions[e.id] = e.definitions || [{ q: '', a: '' }];
      newCover[e.id] = e.cover;
    });
    setPrompt(newPrompt);
    setInstruction(newInstruction);
    setDefinitions(newDefinitions);
    setCover(newCover);
  }, [value]);

  useEffect(() => {
    getOptions();
    document.addEventListener('click', handleDomClick);

    return () => {
      document.removeEventListener('click', handleDomClick);
    };
  }, []);

  const handleDomClick = (e: MouseEvent) => {
    if (selectRef.current && !(
      addRef.current?.contains(e.target as Node)
      || selectRef.current.contains(e.target as Node)
    )) {
      setShowSelect(false);
    }
  };

  const getOptions = async () => {
    setSearchLoading(true);
    const res = await fetchBotsOptions();
    res.list = res.list?.filter(e => !chooseIds.includes(e.value));
    if (res.list) {
      res.list = res.list.map((e) => {
        e.content = getOptionDom(e.label, isAuthor(e.opt?.createdBy));
        return e;
      });
    }
    setOptions(res.list || []);
    setSearchLoading(false);
    return res;
  };

  const getOptionDom = (name: string, isAuthor: boolean) => (
    <div className={styles.option}>
      <div className={styles.name}>{name}</div>
      {isAuthor && <Tag className={classNames(styles.tag, styles.optionTag)}>原创</Tag>}
    </div>
  );

  const checkUsedBot = (botId: string): string | null => {
    let err: string | null = null;
    const bot = usedBots.find(e => e.botId === botId);
    if (bot) {
      const group: { [key: string]: string[] } = {};
      bot.missions.forEach((m) => {
        const { name } = m;
        const type = MissionTypeNameMap[m.type || MissionType.MISSION_TYPE_DEFAULT];
        if (!group[type]) {
          group[type] = [];
        }
        group[type].push(name);
      });

      const list = Object.entries(group).map(([type, names], idx) => {
        if (type === MissionTypeNameMap[MissionType.MISSION_TYPE_PROLOGUE]) {
          return `${idx + 1}. ${type}`;
        }
        const namesList = names.join('，');
        return `${idx + 1}. ${type}: ${namesList}`;
      });
      err = `以下剧情正在使用该角色，请先解除占用：\n${list.join('\n')}\n\n`;
    }

    if (selectedBotIds.includes(botId)) {
      if (!err) {
        err = '';
      }
      err += '群聊功能正在使用该角色，请先去AI玩法页面设置禁言';
    }
    return err;
  };

  const removeBot = (id?: string) => {
    if (!id) return;
    const err = checkUsedBot(id);
    if (err) {
      setErrorMsg(err);
      tipRef.current?.open();
      return;
    }
    if (id === curBotId) closePrompt();
    const newBots = bots.filter(bot => bot.id !== id);
    onChange(newBots);
  };

  const editBot = (id?: string) => {
    if (!id) return;
    setEditId(id);
    setVisible(true);
  };

  const closeDialog = useCallback(() => {
    setShowSelect(false);
    setVisible(false);
    setEditId(undefined);
  }, []);

  const addBot = (id: string, opt: any) => {
    if (chooseIds.includes(id)) return;
    onChange([...bots, { id, botDetail: opt }]);
  };

  const handleBegin = () => {
    setGeneNum(pre => pre + 1);
  };

  const handleComplete = (geneBot: Partial<IBot>) => {
    const { id } = geneBot;
    if (id) {
      const newBot = { id, botDetail: geneBot };
      onChange([...botsRef.current, newBot]);
    }
    setGeneNum(pre => pre - 1);
  };

  const confirmDialog = (botInfo: Partial<IBot>) => {
    const { id } = botInfo;
    if (id) {
      const updatedBots = botsRef.current?.map(bot => (bot.id === id
        ? { ...bot, botDetail: botInfo }
        : bot));
      onChange(updatedBots);
    }
  };

  const handleConfirm = () => {
    if (!curBotId) return;
    const newBots = bots.map((e) => {
      if (e.id === curBotId) {
        return {
          ...e,
          prompt: tempPrompt,
          userInstructionBase: tempInstruction,
          definitions: tempDefinitions,
          cover: tempCover,
        };
      }
      return e;
    });
    onChange(newBots);
  };

  // 自动保存人设
  const autoSave = useCallback(() => {
    handleConfirm();
  }, [curBotId, handleConfirm]);

  UseAutoSave({ save: autoSave, start: true, timeout: 20 });

  const triggerPrompt = (bot: Partial<IBot>) => {
    handleConfirm();
    if (bot.id !== curBotId) {
      openPrompt(bot);
    } else {
      closePrompt();
    }
  };

  const openPrompt = (bot: Partial<IBot>) => {
    const botId = bot.id || '';
    setCurBotId(botId);
    setTempPrompt(prompt[botId]);
    setTempInstruction(instruction[botId]);
    setTempDefinitions(definitions[botId]);
    setTempCover(cover[botId]);
    setShowPrompt(true);
  };

  const closePrompt = () => {
    setCurBotId('');
    setTempPrompt('');
    setTempInstruction('');
    setTempDefinitions([{ q: '', a: '' }]);
    setTempCover(false);
    setShowPrompt(false);
  };

  const hasPrompt = (bot: Partial<IBot>) => {
    const {
      prompt = '',
      userInstructionBase = '',
      definitions = [],
      cover = false,
    } = bot || {};

    return prompt
      || userInstructionBase
      || definitions.filter(e => e.q || e.a).length > 0
      || cover;
  };

  const [errorMsg, setErrorMsg] = useState('');
  const tipRef = useRef<ITipDialogRef>(null);

  const getAvatarDom = (bot: Partial<IBot>) => <>
    <Image
      loading={'...'}
      style={{ width: 80, height: 80, borderRadius: 83 }}
      fit='cover'
      src={bot.botDetail?.avatar?.url?.url || empty}
    />
    {isAuthor(bot.botDetail?.createdBy || '')
      && <Tag className={styles.tag}
        style={{ position: 'absolute', top: 0, right: 0, zIndex: 999 }}
      >原创</Tag>}
    {bot.id && <div id={`op-${bot.id}`}
      className={classNames(styles.op, { [styles.opShow]: bot.id === curBotId })}
      style={{ flexDirection: 'row', gap: 4 }}>
      <Button
        icon={<Edit1Icon size={16} />}
        className={styles.opBtnSmall}
        variant="text"
        onClick={() => editBot(bot.id)}
        disabled={disabled || !isAuthor(bot.botDetail?.createdBy || '')}
      />
      <Button
        icon={<DeleteIcon size={16} />}
        className={styles.opBtnSmall}
        variant="text"
        disabled={disabled || !nodeCanDelete(bot)}
        onClick={() => removeBot(bot.id)}
      />
      {!(hasPrompt(bot) && curBotId !== bot.id)
        && <div className={styles.addPromptInner}>
          <Button
            theme={hasPrompt(bot) ? 'primary' : 'default'}
            style={{ width: 74, height: 24, borderRadius: 4 }}
            onClick={() => triggerPrompt(bot)}
          >
            <div className={styles.btnText}>
              补充人设
              {showPrompt && curBotId === bot.id ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </div>
          </Button>
          {showPrompt && curBotId === bot.id && <Button
            style={{ width: 24, height: 24, padding: 0 }}
            onClick={() => triggerPrompt(bot)}
          >
            <CheckIcon size={16} />
          </Button>}
        </div>}
    </div>}
  </>;

  return (
    <div className={styles.container}>
      <TipDialog tipRef={tipRef} message={errorMsg} />

      <div className={styles.botContent}>
        {bots.map((bot: Partial<IBot>, index: number) => (
          <div key={index} className={styles.bot} title={bot.botDetail?.name}>
            <div className={styles.avatar}>
              {getAvatarDom(bot)}
            </div>
            <div className={styles.nameWrapper}>
              <span className={styles.name}>
                {bot.botDetail?.name || '未命名'}
              </span>
            </div>
            <div className={styles.addPromptOuter}>
              {(hasPrompt(bot) && curBotId !== bot.id)
                && <div className={styles.addPromptOuterBtns}>
                  <Button
                    style={{ width: 74, height: 24, borderRadius: 4 }}
                    onClick={() => triggerPrompt(bot)}
                  >
                    <div className={styles.btnText}>
                      补充人设
                      {showPrompt && curBotId === bot.id ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    </div>
                  </Button>
                  {showPrompt && curBotId === bot.id && <Button
                    style={{ width: 24, height: 24, padding: 0 }}
                    onClick={() => triggerPrompt(bot)}
                  >
                    <CheckIcon size={16} />
                  </Button>}
                </div>}
            </div>
          </div>
        ))}

        {(geneNum > 0) && [...Array(geneNum)].map((_, i) => (
          <div key={i} className={styles.bot}>
            <div className={styles.avatar}>
              <Loading />
            </div>
            <div className={styles.nameWrapper}>
              <span className={styles.name}>
                生成中...
              </span>
            </div>
          </div>
        ))}

        {!disabled && <div
          ref={addRef}
          className={styles.add}
          onClick={() => setShowSelect(true)}
        >
          <Tooltip
            visible={showSelect}
            destroyOnClose={true}
            placement='bottom-left'
            overlayInnerClassName={styles.customTooltip}
            triggerElement={
              <Button
                className={styles.addBtn}
                shape="circle"
                theme='default'
                icon={<SvgIcon name="add" width="18" height="18" />}
              />
            }
            content={
              <DebounceSelect
                selectRef={selectRef}
                fetchOptions={getOptions}
                defaultOptions={options}
                placeholder='请输入角色名称'
                openUpdateOptions={true}
                firstUpdate={false}
                onlyForm={true}
                dependOwner={true}
                style={{ width: 200 }}
                onChange={(val: string, opt: any) => addBot(val, opt)}
                searchLoading={searchLoading}
                popupVisible={true}
                overlayInnerStyle={{ width: 200 }}
                scroll={{}}
              />
            }
          />
          <span className={styles.name}>添加已有角色</span>
        </div>}
      </div>

      {showPrompt && <div className={styles.promptWrapper}>
        <div>
          <div className={styles.promptTitle}>
            <span>本幕人物信息</span>
            <EasyTip pageId='botInfo3' tipList={tips} moduleId='phasesNew' />
          </div>
          <Textarea
            autosize={{ minRows: 5, maxRows: 5 }}
            placeholder={'人物的身份、性格、关系等基本信息\n提到主角时，请用“用户”称呼'}
            value={tempPrompt}
            onChange={v => setTempPrompt(v)}
            disabled={disabled}
          />
        </div>
        <div>
          <div className={styles.promptTitle}>
            <span>本幕规则限制</span>
            <EasyTip pageId='botInfo3' tipList={tips} moduleId='userInstructionBase' />
          </div>
          <Textarea
            autosize={{ minRows: 5, maxRows: 5 }}
            placeholder={'对人物行为模式的要求\n提到主角时，请用“用户”称呼'}
            value={tempInstruction}
            onChange={v => setTempInstruction(v)}
            disabled={disabled}
          />
        </div>
        <div>
          <div className={styles.promptTitle} style={{ height: 34 }}>
            <span>本幕对话示例</span>
          </div>
          <CustomArrayQA
            value={tempDefinitions}
            onChange={v => setTempDefinitions(v)}
            buttonSize='small'
            disabled={disabled}
          />
        </div>
        <div className={styles.promptFooter}>
          <div style={{ display: 'flex', gap: 8 }}>
            <span>覆盖原人设</span>
            <Switch
              disabled={disabled}
              value={tempCover}
              onChange={v => setTempCover(v)}
            />
            <span style={{ fontSize: 12 }}>仅在本幕生效，不影响原始角色</span>
          </div>
        </div>
      </div>}

      <div className={styles.addPanel}>
        <div className={styles.addTitle}>创建角色</div>
        <BotGeneratorInner
          autoSubmit={true}
          disabled={disabled}
          onBegin={handleBegin}
          onComplete={handleComplete}
          rootStyle={{ width: '100%' }}
        />
        <Button
          theme='default'
          style={{ width: '100%', height: 32, borderRadius: 6 }}
          disabled={disabled}
          onClick={() => {
            setVisible(true);
          }}
        >
          <div className={styles.addBtnText}>
            <NavIcon />手动创建
          </div>
        </Button>
      </div>

      {visible && <BotInfo
        visible={visible}
        id={editId}
        onClose={closeDialog}
        onConfirm={confirmDialog}
      />}
    </div>
  );
};
